import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useProducts } from "../../../hooks/useProducts/useProducts";
import { useAttributes } from "../../../hooks/useAttributes/useAttributes";
import { useBatchedUserGroups } from "../../../hooks/useBatchedUserGroups/useBatchedUserGroups";

import { ProductPermission } from "../../../domain/Permission.interface";
import { ProductAttribute } from "../../../domain/ProductAttribute";
import { IOrganizationProductInstance } from "../../../domain/OrganizationProduct";
import { Role } from "../../../domain/Role";
import { datadogRum } from "@datadog/browser-rum";

import {
    ADMINCENTER_PRODUCT_ID,
    ALL_PAGES_SIZE,
    ALL_PROJECTS_ID,
    ALL_PROJECTS_NAME,
    AMBIGUOUS_PRODUCT_GROUP_ERROR_MESSAGE,
    BATCHED_PAGE_SIZE,
    GROUP_TYPES
} from "../../../constants/constants";

import {
    getOrgPermissionsFromGroupList,
    getProjects,
    orgPermissionListToAccessList
} from "../../../services/AccessListTransforms";

import { ProductDropdown } from "../ProductDropdown/ProductDropdown";
import { InstanceDropdown } from "../InstanceDropdown/InstanceDropdown";
import { ProjectsDropdown } from "../ProjectsDropdown/ProjectsDropdown";
import { RoleSelector } from "../RoleSelector/RoleSelector";

import styles from "./AccessSelections.module.scss";
import classnames from "classnames";
import { SelectedUserGroupAccess } from "../../../domain/SelectedUserGroupAccess";
import { useRoles } from "../../../hooks/useRoles/useRoles";
import {
    getProductGroupResolutionErrorKey,
    getSystemRoles,
    ProductGroupResolution,
    resolveProductGroupForRole
} from "../../../lib/access-helpers";
import { Attention } from "@optimizely/axiom";

type AccessSelectionsProps = {
    organizationId: string;
    onRoleSelect: Dispatch<SetStateAction<SelectedUserGroupAccess | undefined>>;
};

export const AccessSelections = ({ organizationId, onRoleSelect }: AccessSelectionsProps) => {
    const { products = [], instances = [] } = useProducts({ organizationId });

    const [selectedProductId, setSelectedProductId] = useState("");
    const [selectedInstanceId, setSelectedInstanceId] = useState("");
    const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
    const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined);
    const [availableGroupRoles, setAvailableGroupRoles] = useState<ProductPermission[] | undefined>(undefined);
    const [unresolvableGroup, setUnresolvableGroup] = useState(false);

    const orgIdRef = useRef<string | undefined>(organizationId);
    const selectedInstanceIdRef = useRef<string | undefined>(selectedInstanceId);

    const {
        userGroups,
        isLoadingMore,
        noMoreData: userGroupsFullyRetrieved
    } = useBatchedUserGroups({
        organizationId: !!selectedInstanceId ? organizationId : undefined,
        groupTypes: [GROUP_TYPES.PRODUCT],
        instanceIds: !!selectedInstanceId ? [selectedInstanceId] : [],
        skip: orgIdRef.current !== organizationId || selectedInstanceIdRef.current !== selectedInstanceId,
        pageSize: BATCHED_PAGE_SIZE
    });

    const { getExperimentationProjects, isLoadingInitialData } = useAttributes({});

    useEffect(() => {
        let cancelled = false;
        const abortController = new AbortController();

        if (!!userGroupsFullyRetrieved && userGroups && products.length) {
            const orgPermissions = getOrgPermissionsFromGroupList(userGroups, products);
            const promiseList = getProjects(orgPermissions, getExperimentationProjects);

            Promise.all(promiseList)
                .then(() => {
                    if (!cancelled && !abortController.signal.aborted) {
                        const list = orgPermissionListToAccessList(orgPermissions);
                        if (list) setAvailableGroupRoles(list);
                    }
                })
                .catch((error) => {
                    // Don't log errors if the component was unmounted or request was aborted
                    if (!cancelled && !abortController.signal.aborted) {
                        console.error("Failed to load projects:", error);
                        datadogRum.addError(error, { context: "AccessSelections.getProjects" });
                    }
                });
        }

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [userGroups, products, getExperimentationProjects, userGroupsFullyRetrieved]);

    const isExp = selectedProductId === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID;

    const roleList = useMemo(() => {
        if (!availableGroupRoles) return [];

        // Scope to the selected instance here, not to "the group touches the selected instance".
        // orgPermissionListToAccessList buckets availableRoles per instance, so flattening every
        // instance of the product and then filtering on the group let a group that spans two
        // Experimentation instances pull instance B's roles into instance A's picker.
        return availableGroupRoles
            .filter((gr) => gr.productId === selectedProductId)
            .flatMap((gr) =>
                gr.instanceAccess
                    .filter((i: any) => i.instance?.optiIdEnabled && i.instance?.id === selectedInstanceId)
                    .flatMap((i: any) => i.availableRoles || [])
            );
    }, [availableGroupRoles, selectedInstanceId, selectedProductId]);

    const projectList = useMemo(() => {
        const projects = isExp ? roleList?.filter((role) => !!role.project).map((r) => r.project) : [];
        return Array.from(new Map(projects?.map((item) => [item["key"], item])).values());
    }, [isExp, roleList]);

    const updatedProjectList = useMemo(() => {
        const allProjectsOption = {
            id: ALL_PROJECTS_ID,
            name: ALL_PROJECTS_NAME,
            description: "",
            instanceId: "",
            isActive: true,
            key: "",
            type: "",
            values: []
        };
        return [allProjectsOption, ...projectList];
    }, [projectList]);

    const roles = useMemo(() => {
        return Array.from(
            new Set(
                roleList
                    ?.filter((role) => {
                        if (!isExp) return true;
                        return selectedProjectId === ALL_PROJECTS_ID
                            ? role.role.isInstanceRole
                            : !role.role.isInstanceRole && role?.project?.id === selectedProjectId;
                    })
                    .map((r) => r?.role)
            )
        );
    }, [roleList, selectedProjectId, isExp]);

    const roleIdsInGroups = roles.map((role) => role.id);

    // Get all product roles so we can later filter
    const { roles: productRoles } = useRoles({
        organizationId,
        productId: selectedProductId,
        instanceId: selectedInstanceId,
        size: ALL_PAGES_SIZE
    });

    let adjustedRoles = productRoles || [];

    // Admin Center should not show any Project Owner roles or User Manager roles
    // Or any roles that are not system generated
    if (selectedProductId === ADMINCENTER_PRODUCT_ID) {
        adjustedRoles = getSystemRoles({ roles: adjustedRoles, productId: selectedProductId });
    }
    // EXP project roles need to be system generated
    if (isExp && selectedProjectId !== ALL_PROJECTS_ID) {
        adjustedRoles = getSystemRoles({
            roles: adjustedRoles,
            projectId: selectedProjectId,
            productId: selectedProductId
        });
    }

    adjustedRoles = adjustedRoles.filter((r) => roleIdsInGroups.includes(r.id));
    adjustedRoles = adjustedRoles.sort((a, b) => a.displayName.localeCompare(b.displayName));

    const selectedProductInstances = useMemo(() => {
        return instances
            .filter((instance) => instance.optiIdEnabled && instance.productId === selectedProductId)
            .map(
                (instance) =>
                    ({
                        id: instance.instanceId,
                        name: instance.instanceName,
                        nickname: instance.nickname,
                        optiIdEnabled: instance.optiIdEnabled,
                        region: instance.region,
                        technicalContactEmail: instance.technicalContactEmail,
                        technicalContactName: instance.technicalContactName
                    }) as IOrganizationProductInstance
            );
    }, [instances, selectedProductId]);

    // Instance and project scoping both happen inside resolveProductGroupForRole; this only narrows
    // to the selected product so the resolver is not handed unrelated products' access.
    const instanceAccessForSelectedInstance = useMemo(
        () =>
            availableGroupRoles
                ?.filter((gr) => gr.productId === selectedProductId)
                .flatMap((gr) => gr.instanceAccess)
                .filter((ia) => ia.instance?.id === selectedInstanceId) || [],
        [availableGroupRoles, selectedProductId, selectedInstanceId]
    );

    /**
     * Resolves the product group that actually grants `role` on the selected instance, then hands it
     * up for the invitation.
     *
     * Role linking (EPTS-5258) lets several product groups offer the same role on the same instance,
     * so picking the first match grants whatever the API happened to return first (EPTS-6364). On
     * this flow that lands in `createInvitation({ requireAcceptance: false })`, granting immediately
     * into a customer org, so anything that does not resolve to exactly one group clears the
     * selection instead — the Save button is already gated on a selection being present.
     */
    const selectGroupForRole = useCallback(
        (role: Role) => {
            const instance = selectedProductInstances.find((i) => i.id === selectedInstanceId);
            const resolution: ProductGroupResolution = instance
                ? resolveProductGroupForRole({
                      instance,
                      instanceAccess: instanceAccessForSelectedInstance,
                      projectId: selectedProjectId,
                      role
                  })
                : { status: "none", candidateGroupIds: [] };

            if (resolution.status !== "resolved") {
                setUnresolvableGroup(true);
                onRoleSelect(undefined);
                datadogRum.addError(new Error(getProductGroupResolutionErrorKey(resolution.status)), {
                    organizationId,
                    instanceId: selectedInstanceId,
                    roleId: role.id,
                    resolution: resolution.status,
                    candidateGroupIds: resolution.candidateGroupIds,
                    candidateCount: resolution.candidateGroupIds.length,
                    source: "AccessSelections.selectGroupForRole"
                });
                return;
            }

            const productInstance = instances.find((i) => i.instanceId === selectedInstanceId);
            const project = selectedProjectId && updatedProjectList.find((p) => p.id === selectedProjectId);

            setUnresolvableGroup(false);
            onRoleSelect({
                id: resolution.match.group.id,
                name: resolution.match.group.name,
                productName: productInstance?.productName,
                instanceName: productInstance?.nickname || productInstance?.instanceName,
                projectName: project ? project.name : undefined,
                roleName: role.displayName || role.name
            });
        },
        [
            instanceAccessForSelectedInstance,
            instances,
            onRoleSelect,
            organizationId,
            selectedInstanceId,
            selectedProductInstances,
            selectedProjectId,
            updatedProjectList
        ]
    );

    // any change to the selection invalidates a previous resolution failure
    useEffect(() => {
        setUnresolvableGroup(false);
    }, [selectedProductId, selectedInstanceId, selectedProjectId]);

    // Reset dropdown and role state when organizationId changes
    useEffect(() => {
        orgIdRef.current = organizationId;
        selectedInstanceIdRef.current = "";
        // Reset all dependent states
        setSelectedProductId("");
        setSelectedInstanceId("");
        setSelectedProjectId(undefined);
        setSelectedRole(undefined);
        onRoleSelect(undefined);
        setAvailableGroupRoles(undefined);
    }, [organizationId, onRoleSelect]);

    // auto select single dropdown options (projects omitted)
    useEffect(() => {
        if (products.length === 1) {
            setSelectedProductId(products[0].id);
        }
    }, [products]);

    useEffect(() => {
        if (!!selectedProductId && selectedProductInstances.length === 1) {
            setSelectedInstanceId(selectedProductInstances[0].id);
            selectedInstanceIdRef.current = selectedProductInstances[0].id;
        }
    }, [selectedProductInstances, selectedProductId]);

    // Gated on userGroupsFullyRetrieved: availableGroupRoles is kept across an instance change, and
    // a group fetched for a sibling instance still contributes an instanceAccess entry for this one.
    // Auto-selecting before the new instance's groups have all arrived would resolve against a
    // partial candidate set, and the Save button is gated only on the resulting selection.
    useEffect(() => {
        if (!!selectedInstanceId && userGroupsFullyRetrieved && roles.length === 1) {
            const onlyRole = roles[0];
            setSelectedRole(onlyRole);
            selectGroupForRole(onlyRole);
        }
    }, [roles, selectedInstanceId, userGroupsFullyRetrieved, selectGroupForRole]);

    const resetRoleSelection = () => {
        if (!!selectedRole) {
            onRoleSelect(undefined);
            setSelectedRole(undefined);
        }
    };

    // dropdown selection handlers
    const handleProductChange = (e: { product: { id: string } }) => {
        setSelectedProductId(e.product.id);
        // reset dependent states
        setSelectedInstanceId("");
        setSelectedProjectId(undefined);
        resetRoleSelection();
    };

    const handleInstanceSelection = ({
        value
    }: {
        value: IOrganizationProductInstance | IOrganizationProductInstance[] | null;
    }) => {
        const instance = value as IOrganizationProductInstance;
        setSelectedInstanceId(instance?.id);
        selectedInstanceIdRef.current = instance?.id;
        // reset dependent states
        setSelectedProjectId(undefined);
        resetRoleSelection();
    };

    const handleProjectChange = ({ value }: { value: ProductAttribute | ProductAttribute[] | null }) => {
        const project = value as ProductAttribute;
        setSelectedProjectId(project.id);
        // reset dependent states
        resetRoleSelection();
    };

    const handleRoleChange = (e: Role) => {
        setSelectedRole(e);
        selectGroupForRole(e);
    };

    const dropdownClasses = classnames(
        "dropdown-inherit-width",
        styles["access-selections-form__selection-row-dropdown"]
    );

    return (
        <div className={styles["access-selections-form"]}>
            <div className="flex push--bottom push--top">
                <ProductDropdown
                    disabled={!organizationId || !products}
                    onChange={handleProductChange}
                    products={products}
                    white
                    value={selectedProductId}
                    className={dropdownClasses}
                />
                <InstanceDropdown
                    className={dropdownClasses}
                    disabled={!selectedProductId}
                    instances={selectedProductInstances}
                    onChange={handleInstanceSelection}
                    value={selectedInstanceId}
                    white
                />
            </div>
            {selectedProductId && !selectedProductInstances.length && (
                <Attention alignment="left" className="push--top push--bottom" type="warning">
                    This organization does not have any Opti ID enabled instances for this product.
                </Attention>
            )}
            {isExp && (
                <div className="push--bottom">
                    <ProjectsDropdown
                        className={dropdownClasses}
                        disabled={!selectedInstanceId}
                        loading={!!selectedInstanceId && (!!isLoadingInitialData || isLoadingMore)}
                        onChange={handleProjectChange}
                        projects={updatedProjectList}
                        value={selectedProjectId}
                        white
                    />
                </div>
            )}
            <RoleSelector
                isDisabled={
                    !selectedProductId ||
                    !selectedInstanceId ||
                    (isExp && !selectedProjectId) ||
                    !userGroupsFullyRetrieved
                }
                loading={
                    isExp
                        ? !!selectedProjectId && !!isLoadingInitialData
                        : !!selectedInstanceId && !userGroupsFullyRetrieved
                }
                className={dropdownClasses}
                selectedRole={selectedRole}
                fullWidth={false}
                onRoleChanged={handleRoleChange}
                roles={adjustedRoles}
                isInitialized={
                    isExp
                        ? !!selectedProjectId
                        : !!selectedInstanceId && availableGroupRoles !== undefined && !!userGroupsFullyRetrieved
                }
            />
            {unresolvableGroup && (
                <Attention
                    alignment="left"
                    className="push--top"
                    type="bad-news"
                    data-testid="unresolvable-group-error"
                >
                    {AMBIGUOUS_PRODUCT_GROUP_ERROR_MESSAGE}
                </Attention>
            )}
        </div>
    );
};

AccessSelections.displayName = "SupportAccessSelections";
